import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/Toast';
import {
  campaignRecommendationFingerprint,
  dismissRecommendations,
  filterDismissedRows,
  loadDismissedRecommendations,
} from '@/api/recommendationDismiss';
import { filterBySearch } from '@/api/recommendationUtils';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Loader2,
  Pause,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { BudgetIncreaseModal } from '@/components/recommendations/BudgetIncreaseModal';
import { PauseConfirmModal } from '@/components/recommendations/PauseConfirmModal';
import { SyncReportsButton } from '@/components/SyncReportsButton';
import { adsApi } from '@/api/client';
import {
  REPORT_AD_PRODUCT_OPTIONS,
  reportAdProductShortLabel,
  reportAdProductsQueryParam,
} from '@/api/reportAdProducts';
import {
  type DateRange,
} from '@/api/reportDateRanges';
import {
  DEFAULT_BUDGET_INCREASE_PCT,
  enrichHighPerformerRow,
  enrichLowPerformerRow,
} from '@/api/campaignRecommendationProjections';
import type {
  CampaignRecommendationsData,
  RecommendationCampaignRow,
} from '@/types/campaignRecommendations';
import type { CampaignAdProduct } from '@/types/spCampaigns';

import { formatCurrency as formatAccountCurrency } from '@/utils/formatCurrency';

function formatCurrency(amount: number) {
  return formatAccountCurrency(amount);
}

function formatNumberValue(value: number | null, fractionDigits = 2): string {
  if (value === null) return '-';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function campaignTypeToAdProduct(campaignType: string): CampaignAdProduct {
  if (campaignType === 'SB') return 'SB';
  if (campaignType === 'SD') return 'SD';
  return 'SP';
}

function profileCampaignDetailPath(
  profileId: string,
  campaignId: string,
  adProduct: CampaignAdProduct
): string {
  const base = `/accounts/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}`;
  if (adProduct !== 'SP') return `${base}?product=${encodeURIComponent(adProduct)}`;
  return base;
}

function parseRecommendationsPayload(data: unknown): CampaignRecommendationsData {
  if (!data || typeof data !== 'object') return {};
  const raw = data as Record<string, unknown>;
  const num = (v: unknown) => {
    if (v === null || v === undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const pick = (r: Record<string, unknown>, camel: string, snake: string) =>
    r[camel] ?? r[snake];

  const parseRows = (
    rows: unknown,
    variant: 'high' | 'low',
    defaultPct: number
  ): RecommendationCampaignRow[] => {
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
      .map((r) => {
        const cost = Number(pick(r, 'cost', 'cost') ?? 0);
        const sales30d = Number(pick(r, 'sales30d', 'sales_30d') ?? 0);
        const roasFromApi = num(pick(r, 'roas', 'roas'));
        const roas =
          roasFromApi != null && roasFromApi > 0
            ? roasFromApi
            : cost > 0
              ? sales30d / cost
              : 0;
        const base: RecommendationCampaignRow = {
          adProduct: String(pick(r, 'adProduct', 'ad_product') ?? ''),
          campaignType: String(pick(r, 'campaignType', 'campaign_type') ?? 'SP'),
          campaignId: String(pick(r, 'campaignId', 'campaign_id') ?? ''),
          campaignName: String(pick(r, 'campaignName', 'campaign_name') ?? '-'),
          state: String(r.state ?? ''),
          impressions: Number(r.impressions ?? 0),
          clicks: Number(r.clicks ?? 0),
          cost,
          sales30d,
          purchases30d: Number(pick(r, 'purchases30d', 'purchases_30d') ?? 0),
          roas,
          acos:
            pick(r, 'acos', 'acos') === null || pick(r, 'acos', 'acos') === undefined
              ? null
              : Number(pick(r, 'acos', 'acos')),
          ctr: Number(r.ctr ?? 0),
          dailyBudget:
            pick(r, 'dailyBudget', 'daily_budget') === null ||
            pick(r, 'dailyBudget', 'daily_budget') === undefined
              ? null
              : Number(pick(r, 'dailyBudget', 'daily_budget')),
          budgetType: String(pick(r, 'budgetType', 'budget_type') ?? 'DAILY'),
          suggestedBudget:
            pick(r, 'suggestedBudget', 'suggested_budget') === null ||
            pick(r, 'suggestedBudget', 'suggested_budget') === undefined
              ? null
              : Number(pick(r, 'suggestedBudget', 'suggested_budget')),
          rationale: String(r.rationale ?? ''),
        };
        return variant === 'high'
          ? enrichHighPerformerRow(base, defaultPct)
          : enrichLowPerformerRow(base);
      });
  };
  const thresholdsRaw = raw.thresholds;
  const thresholds =
    thresholdsRaw && typeof thresholdsRaw === 'object'
      ? {
          minCost: Number((thresholdsRaw as Record<string, unknown>).minCost ?? 0),
          minClicks: Number((thresholdsRaw as Record<string, unknown>).minClicks ?? 0),
          medianRoas:
            (thresholdsRaw as Record<string, unknown>).medianRoas === null ||
            (thresholdsRaw as Record<string, unknown>).medianRoas === undefined
              ? null
              : Number((thresholdsRaw as Record<string, unknown>).medianRoas),
          highRoasMin:
            (thresholdsRaw as Record<string, unknown>).highRoasMin === null ||
            (thresholdsRaw as Record<string, unknown>).highRoasMin === undefined
              ? null
              : Number((thresholdsRaw as Record<string, unknown>).highRoasMin),
          lowRoasMax:
            (thresholdsRaw as Record<string, unknown>).lowRoasMax === null ||
            (thresholdsRaw as Record<string, unknown>).lowRoasMax === undefined
              ? null
              : Number((thresholdsRaw as Record<string, unknown>).lowRoasMax),
          budgetIncreasePct: num(
            (thresholdsRaw as Record<string, unknown>).budgetIncreasePct ??
              (thresholdsRaw as Record<string, unknown>).budget_increase_pct
          ),
        }
      : undefined;
  const defaultPct = thresholds?.budgetIncreasePct ?? DEFAULT_BUDGET_INCREASE_PCT;
  const rangeRaw = raw.range;
  const range =
    rangeRaw && typeof rangeRaw === 'object'
      ? {
          startDate: String((rangeRaw as Record<string, unknown>).startDate ?? ''),
          endDate: String((rangeRaw as Record<string, unknown>).endDate ?? ''),
        }
      : undefined;
  return {
    range,
    noData: Boolean(raw.noData),
    eligibleCount: Number(raw.eligibleCount ?? 0),
    thresholds,
    highPerformers: parseRows(
      raw.highPerformers ?? raw.high_performers,
      'high',
      defaultPct
    ),
    lowPerformers: parseRows(raw.lowPerformers ?? raw.low_performers, 'low', defaultPct),
  };
}


function MetricChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[0.65rem] uppercase tracking-wide text-foreground-subtle">{label}</div>
      <div className="text-sm font-medium text-foreground truncate">{value}</div>
    </div>
  );
}

function HighPerformerCard({
  profileId,
  row,
  budgetIncreasePct,
  onIncreaseBudget,
  onDismiss,
}: {
  profileId: string;
  row: RecommendationCampaignRow;
  budgetIncreasePct: number;
  onIncreaseBudget: (row: RecommendationCampaignRow) => void;
  onDismiss: (row: RecommendationCampaignRow) => void;
}) {
  const adProduct = campaignTypeToAdProduct(row.campaignType);
  const isSp = adProduct === 'SP';
  const detailPath = profileCampaignDetailPath(profileId, row.campaignId, adProduct);
  const pct = row.budgetIncreasePct ?? budgetIncreasePct;
  const earnings = row.projectedAdditionalEarnings ?? 0;
  const extraSales = row.projectedAdditionalSales ?? 0;
  const extraCost = row.projectedAdditionalCost ?? 0;
  const showEarnings = earnings > 0;

  return (
    <article className="rounded-xl border border-success/20 bg-surface p-4 shadow-sm flex flex-col gap-3 min-w-0">
      <div className="min-w-0">
        <Link
          to={detailPath}
          className="font-semibold text-brand hover:underline line-clamp-2 leading-snug"
        >
          {row.campaignName}
        </Link>
        <p className="text-xs text-foreground-subtle mt-0.5">
          {reportAdProductShortLabel(row.adProduct)} · {row.clicks} clicks
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricChip label="Spend" value={formatCurrency(row.cost)} />
        <MetricChip label="Sales" value={formatCurrency(row.sales30d)} />
        <MetricChip label="ROAS" value={formatNumberValue(row.roas)} />
      </div>

      <div className="rounded-xl bg-success-subtle/80 border border-success/20 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <ArrowUpRight className="w-4 h-4 text-success-ink shrink-0 mt-0.5" />
          <div className="min-w-0 text-sm text-success-ink">
            <p className="font-medium">
              +{pct}% budget → about{' '}
              {showEarnings ? formatCurrency(earnings) : formatCurrency(0)} more earnings
            </p>
            {showEarnings && (
              <p className="text-xs text-success-ink mt-1">
                Est. {formatCurrency(extraSales)} extra sales on {formatCurrency(extraCost)} extra
                spend (ROAS {formatNumberValue(row.roas)} from this period).
              </p>
            )}
            {!showEarnings && row.cost > 0 && (
              <p className="text-xs text-success-ink mt-1">
                Earnings estimate needs sales above spend in this period (ROAS &gt; 1).
              </p>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground-muted leading-relaxed">{row.rationale}</p>

      <button
        type="button"
        disabled={!isSp}
        onClick={() => onIncreaseBudget(row)}
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-success text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        title={!isSp ? 'Budget updates are available for Sponsored Products only' : undefined}
      >
        <TrendingUp className="w-4 h-4" />
        Increase budget (+{pct}%)
      </button>
      {!isSp && <p className="text-[0.65rem] text-warning-ink text-center">SP actions only</p>}
      <button
        type="button"
        onClick={() => onDismiss(row)}
        className="text-xs text-foreground-subtle hover:text-foreground-muted hover:underline"
      >
        Dismiss
      </button>
    </article>
  );
}

function LowPerformerCard({
  profileId,
  row,
  onPause,
  onDismiss,
}: {
  profileId: string;
  row: RecommendationCampaignRow;
  onPause: (row: RecommendationCampaignRow) => void;
  onDismiss: (row: RecommendationCampaignRow) => void;
}) {
  const adProduct = campaignTypeToAdProduct(row.campaignType);
  const isSp = adProduct === 'SP';
  const detailPath = profileCampaignDetailPath(profileId, row.campaignId, adProduct);
  const savings = row.projectedSavings ?? row.cost;
  const lossAvoided = row.projectedLossAvoided ?? 0;

  return (
    <article className="rounded-xl border border-warning/20 bg-surface p-4 shadow-sm flex flex-col gap-3 min-w-0">
      <div className="min-w-0">
        <Link
          to={detailPath}
          className="font-semibold text-brand hover:underline line-clamp-2 leading-snug"
        >
          {row.campaignName}
        </Link>
        <p className="text-xs text-foreground-subtle mt-0.5">
          {reportAdProductShortLabel(row.adProduct)} · {row.clicks} clicks
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricChip label="Spend" value={formatCurrency(row.cost)} />
        <MetricChip label="Sales" value={formatCurrency(row.sales30d)} />
        <MetricChip label="ROAS" value={formatNumberValue(row.roas)} />
      </div>

      <div className="rounded-xl bg-warning-subtle/80 border border-warning/20 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <ArrowDownRight className="w-4 h-4 text-warning-ink shrink-0 mt-0.5" />
          <div className="min-w-0 text-sm text-warning-ink">
            <p className="font-medium">Pause → save about {formatCurrency(savings)} in ad spend</p>
            {lossAvoided > 0 ? (
              <p className="text-xs text-warning-ink mt-1">
                Includes ~{formatCurrency(lossAvoided)} net loss avoided (spend minus sales) over a
                similar period.
              </p>
            ) : (
              <p className="text-xs text-warning-ink mt-1">
                Stops future spend for this campaign over a similar period.
              </p>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground-muted leading-relaxed">{row.rationale}</p>

      <button
        type="button"
        disabled={!isSp}
        onClick={() => onPause(row)}
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-warning text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        title={!isSp ? 'Pause is available for Sponsored Products only' : undefined}
      >
        <Pause className="w-4 h-4" />
        Pause campaign
      </button>
      {!isSp && <p className="text-[0.65rem] text-warning-ink text-center">SP actions only</p>}
      <button
        type="button"
        onClick={() => onDismiss(row)}
        className="text-xs text-foreground-subtle hover:text-foreground-muted hover:underline"
      >
        Dismiss
      </button>
    </article>
  );
}

function RecommendationColumn({
  profileId,
  title,
  subtitle,
  variant,
  rows,
  totalCount,
  budgetIncreasePct,
  onIncreaseBudget,
  onPause,
  onDismiss,
  headerClass,
  icon,
}: {
  profileId: string;
  title: string;
  subtitle: string;
  variant: 'high' | 'low';
  rows: RecommendationCampaignRow[];
  totalCount: number;
  budgetIncreasePct: number;
  onIncreaseBudget: (row: RecommendationCampaignRow) => void;
  onPause: (row: RecommendationCampaignRow) => void;
  onDismiss: (row: RecommendationCampaignRow) => void;
  headerClass: string;
  icon: React.ReactNode;
}) {
  return (
    <section className="flex flex-col min-w-0 rounded-xl border border-border bg-page/50 overflow-hidden">
      <div className={`px-4 py-3 border-b flex items-start gap-2 ${headerClass}`}>
        {icon}
        <div className="min-w-0">
          <h3 className="font-semibold">{title}</h3>
          <p className="text-xs opacity-90 mt-0.5">{subtitle}</p>
          <p className="mt-1 text-xs opacity-80">
            Showing {rows.length} of {totalCount}
          </p>
        </div>
      </div>
      <div className="p-3">
        {rows.length === 0 ? (
          <p className="text-sm text-foreground-subtle py-8 text-center px-2">
            No campaigns match the criteria for this column in the selected period.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {rows.map((row) => (
              <li key={`${row.adProduct}-${row.campaignId}`}>
                {variant === 'high' ? (
                  <HighPerformerCard
                    profileId={profileId}
                    row={row}
                    budgetIncreasePct={budgetIncreasePct}
                    onIncreaseBudget={onIncreaseBudget}
                    onDismiss={onDismiss}
                  />
                ) : (
                  <LowPerformerCard
                    profileId={profileId}
                    row={row}
                    onPause={onPause}
                    onDismiss={onDismiss}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export function CampaignRecommendationsTab({
  profileId,
  range,
  rangeOk,
}: {
  profileId: string;
  range: DateRange;
  rangeOk: boolean;
}) {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [adProductFilters] = useState<string[]>(() =>
    REPORT_AD_PRODUCT_OPTIONS.map((o) => o.id)
  );
  const [budgetRow, setBudgetRow] = useState<RecommendationCampaignRow | null>(null);
  const [pauseRow, setPauseRow] = useState<RecommendationCampaignRow | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(() =>
    loadDismissedRecommendations(profileId)
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [highSortBy, setHighSortBy] = useState<'earnings' | 'roas' | 'cost'>('earnings');
  const [lowSortBy, setLowSortBy] = useState<'savings' | 'roas' | 'cost'>('savings');

  const adProductsParam = useMemo(
    () => reportAdProductsQueryParam(adProductFilters),
    [adProductFilters]
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'campaign-recommendations',
      profileId,
      range.startDate,
      range.endDate,
      adProductsParam,
    ],
    queryFn: async () => {
      const res = await adsApi.getCampaignRecommendations(profileId, {
        startDate: range.startDate,
        endDate: range.endDate,
        adProducts: adProductsParam,
      });
      if (!res.success) throw new Error('Failed to load recommendations');
      return parseRecommendationsPayload(res.data);
    },
    enabled: !!profileId && rangeOk,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['campaign-recommendations', profileId] });
    void qc.invalidateQueries({ queryKey: ['profile-campaigns', profileId] });
  };

  const thresholds = data?.thresholds;
  const budgetIncreasePct = thresholds?.budgetIncreasePct ?? 20;

  const handleDismissCampaign = useCallback(
    (row: RecommendationCampaignRow, variant: 'high' | 'low') => {
      const fingerprint = campaignRecommendationFingerprint(row.adProduct, row.campaignId, variant);
      const next = dismissRecommendations(profileId, [fingerprint]);
      setDismissed(next);
      showToast(`Dismissed ${row.campaignName}.`, 'success');
    },
    [profileId, showToast]
  );

  const highPerformersRaw = useMemo(
    () =>
      filterDismissedRows(data?.highPerformers ?? [], dismissed, (row) =>
        campaignRecommendationFingerprint(row.adProduct, row.campaignId, 'high')
      ),
    [data?.highPerformers, dismissed]
  );

  const lowPerformersRaw = useMemo(
    () =>
      filterDismissedRows(data?.lowPerformers ?? [], dismissed, (row) =>
        campaignRecommendationFingerprint(row.adProduct, row.campaignId, 'low')
      ),
    [data?.lowPerformers, dismissed]
  );

  const sortedHighPerformers = useMemo(() => {
    const searched = filterBySearch(highPerformersRaw, searchQuery, (row) => [
      row.campaignName,
      row.campaignId,
    ]);
    const sorted = [...searched].sort((a, b) => {
      if (highSortBy === 'earnings') {
        return (a.projectedAdditionalEarnings ?? 0) - (b.projectedAdditionalEarnings ?? 0);
      }
      if (highSortBy === 'roas') return a.roas - b.roas;
      return a.cost - b.cost;
    });
    return sorted.reverse();
  }, [highPerformersRaw, searchQuery, highSortBy]);

  const sortedLowPerformers = useMemo(() => {
    const searched = filterBySearch(lowPerformersRaw, searchQuery, (row) => [
      row.campaignName,
      row.campaignId,
    ]);
    const sorted = [...searched].sort((a, b) => {
      if (lowSortBy === 'savings') {
        return (a.projectedSavings ?? a.cost) - (b.projectedSavings ?? b.cost);
      }
      if (lowSortBy === 'roas') return a.roas - b.roas;
      return a.cost - b.cost;
    });
    return sorted.reverse();
  }, [lowPerformersRaw, searchQuery, lowSortBy]);

  const highTotalEarnings = useMemo(
    () =>
      sortedHighPerformers.reduce((sum, r) => sum + (r.projectedAdditionalEarnings ?? 0), 0),
    [sortedHighPerformers]
  );

  const lowTotalSavings = useMemo(
    () => sortedLowPerformers.reduce((sum, r) => sum + (r.projectedSavings ?? r.cost), 0),
    [sortedLowPerformers]
  );

  return (
    <div className="space-y-6 min-w-0">
      {thresholds && (
        <div className="rounded-xl border border-border bg-page px-4 py-3 text-sm text-foreground-muted">
          <p>
            Only <strong>ENABLED</strong> campaigns with at least{' '}
            <strong>{formatCurrency(thresholds.minCost)}</strong> spend and{' '}
            <strong>{thresholds.minClicks}</strong> clicks are scored.
            {thresholds.medianRoas != null && (
              <>
                {' '}
                Profile median ROAS: <strong>{formatNumberValue(thresholds.medianRoas)}</strong>.
                High: ROAS ≥ <strong>{formatNumberValue(thresholds.highRoasMin)}</strong>. Low: ROAS
                &lt; <strong>{formatNumberValue(thresholds.lowRoasMax)}</strong> or spend exceeds
                sales.
              </>
            )}
          </p>
          <p className="text-foreground-subtle mt-1 text-xs">
            Projections assume the same ROAS and spend efficiency as the selected period. Budget
            uplift uses +{budgetIncreasePct}% by default.
            {data?.eligibleCount != null && ` ${data.eligibleCount} eligible campaigns.`}
          </p>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-foreground-muted py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading recommendations…
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-3 text-sm text-error-ink">
          {(error as Error).message}
          <button type="button" onClick={() => refetch()} className="ml-3 underline">
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && data?.noData && (
        <div className="rounded-xl border border-warning/20 bg-warning-subtle px-4 py-6 text-center">
          <AlertTriangle className="w-8 h-8 text-warning mx-auto mb-2" />
          <p className="text-sm text-warning-ink font-medium">No report data for this period</p>
          <p className="text-sm text-warning-ink mt-1 mb-4">
            Sync Sponsored Ads reports first, then refresh this page for recommendations.
          </p>
          <SyncReportsButton
            profileId={profileId}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white"
          />
        </div>
      )}

      {!isLoading && !error && data && !data.noData && (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-page/80 px-3 py-2">
            <div className="min-w-[12rem] flex-1">
              <label className="mb-1 block text-xs font-medium text-foreground-muted">Search campaigns</label>
              <input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Campaign name…"
                className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground-muted">Sort high performers</label>
              <select
                value={highSortBy}
                onChange={(event) =>
                  setHighSortBy(event.target.value as 'earnings' | 'roas' | 'cost')
                }
                className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
              >
                <option value="earnings">Earnings</option>
                <option value="roas">ROAS</option>
                <option value="cost">Spend</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-foreground-muted">Sort low performers</label>
              <select
                value={lowSortBy}
                onChange={(event) => setLowSortBy(event.target.value as 'savings' | 'roas' | 'cost')}
                className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
              >
                <option value="savings">Savings</option>
                <option value="roas">ROAS</option>
                <option value="cost">Spend</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0">
            <RecommendationColumn
              profileId={profileId}
              title="Add more funds"
              subtitle={
                highTotalEarnings > 0
                  ? `Up to ~${formatCurrency(highTotalEarnings)} extra earnings if top picks scale +${budgetIncreasePct}%`
                  : `Strong ROAS — +${budgetIncreasePct}% budget may capture more sales`
              }
              variant="high"
              rows={sortedHighPerformers}
              totalCount={highPerformersRaw.length}
              budgetIncreasePct={budgetIncreasePct}
              onIncreaseBudget={setBudgetRow}
              onPause={() => {}}
              onDismiss={(row) => handleDismissCampaign(row, 'high')}
              headerClass="bg-success-subtle border-success/20 text-success-ink"
              icon={<TrendingUp className="w-5 h-5 text-success-ink shrink-0" />}
            />
            <RecommendationColumn
              profileId={profileId}
              title="Reduce loss"
              subtitle={
                lowTotalSavings > 0
                  ? `Up to ~${formatCurrency(lowTotalSavings)} ad spend avoidable by pausing listed campaigns`
                  : 'Weak ROAS — pausing stops spend without proportional returns'
              }
              variant="low"
              rows={sortedLowPerformers}
              totalCount={lowPerformersRaw.length}
              budgetIncreasePct={budgetIncreasePct}
              onIncreaseBudget={() => {}}
              onPause={setPauseRow}
              onDismiss={(row) => handleDismissCampaign(row, 'low')}
              headerClass="bg-warning-subtle border-warning/20 text-warning-ink"
              icon={<TrendingDown className="w-5 h-5 text-warning-ink shrink-0" />}
            />
          </div>
        </>
      )}

      {budgetRow && (
        <BudgetIncreaseModal
          profileId={profileId}
          row={budgetRow}
          budgetIncreasePct={budgetIncreasePct}
          isOpen
          onClose={() => setBudgetRow(null)}
          onSuccess={invalidate}
          onToast={showToast}
        />
      )}
      {pauseRow && (
        <PauseConfirmModal
          profileId={profileId}
          row={pauseRow}
          isOpen
          onClose={() => setPauseRow(null)}
          onSuccess={invalidate}
          onToast={showToast}
        />
      )}
    </div>
  );
}
